import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search, Loader2, Trophy, Users, Calendar, ArrowLeft } from 'lucide-react';
import { api } from '@/api';
import { SEO } from '@/components/common/SEO';

/**
 * Full search results page — /search?q=...
 * Shows all matches grouped by category with up to 50 per group.
 */
export const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryParam = searchParams.get('q') || '';
  const [input, setInput] = useState(queryParam);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { setInput(queryParam); }, [queryParam]);

  useEffect(() => {
    if (!queryParam) { setResults(null); return; }
    let cancelled = false;
    setLoading(true);
    setError('');
    api.search(queryParam, 50)
      .then(res => { if (!cancelled) setResults(res.data); })
      .catch(err => {
        console.error('Search failed', err);
        if (!cancelled) setError(err.response?.data?.detail || 'Search failed');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [queryParam]);

  const onSubmit = (e) => {
    e.preventDefault();
    const q = input.trim();
    if (q) setSearchParams({ q });
  };

  const totals = results?.totals || { teams: 0, leagues: 0, games: 0 };
  const totalAll = totals.teams + totals.leagues + totals.games;

  return (
    <div className="min-h-screen bg-gray-950" data-testid="search-page">
      <SEO
        title={queryParam ? `Search: ${queryParam}` : 'Search'}
        description={`Search WPSL TV for teams, conferences, and games matching "${queryParam}"`}
        url={`/search?q=${encodeURIComponent(queryParam)}`}
      />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white mb-4 transition-colors"
          data-testid="search-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          Search results
        </h1>

        <form onSubmit={onSubmit} className="flex items-center bg-gray-800 border border-gray-700 focus-within:border-sky-500 rounded-full px-4 py-2 mb-6 transition-colors">
          <Search className="w-5 h-5 text-gray-400 shrink-0 mr-3" />
          <input
            autoFocus
            type="search"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Search teams, conferences, games…"
            className="flex-1 bg-transparent text-white outline-none placeholder:text-gray-500"
            data-testid="search-page-input"
          />
          <button
            type="submit"
            className="ml-2 px-4 py-1.5 bg-sky-500 hover:bg-sky-600 text-white text-sm rounded-full transition-colors"
            data-testid="search-page-submit"
          >
            Search
          </button>
        </form>

        {!queryParam && (
          <div className="py-16 text-center text-gray-400">
            Type a team, conference, or game name above to start searching.
          </div>
        )}

        {queryParam && loading && (
          <div className="flex items-center justify-center gap-2 py-12 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" /> Searching for "{queryParam}"…
          </div>
        )}

        {queryParam && error && !loading && (
          <div className="bg-red-900/20 border border-red-600/50 rounded-lg p-4 text-red-300" data-testid="search-page-error">
            {error}
          </div>
        )}

        {queryParam && results && !loading && (
          <>
            {totalAll === 0 ? (
              <div className="py-16 text-center text-gray-400" data-testid="search-page-empty">
                No results for <span className="text-white font-medium">"{queryParam}"</span>.<br />
                Try a different keyword.
              </div>
            ) : (
              <div className="text-sm text-gray-400 mb-4" data-testid="search-page-totals">
                {totalAll} result{totalAll === 1 ? '' : 's'} for <span className="text-white">"{queryParam}"</span>
              </div>
            )}

            {results.teams.length > 0 && (
              <Section title="Teams" count={totals.teams} icon={Users}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {results.teams.map(t => (
                    <ResultCard
                      key={t.id}
                      testId={`search-result-team-${t.id}`}
                      title={t.name}
                      subtitle={t.short_name && t.short_name !== t.name ? t.short_name : ''}
                      image={t.logo}
                      onClick={() => navigate(`/team/${t.slug}`)}
                    />
                  ))}
                </div>
              </Section>
            )}

            {results.leagues.length > 0 && (
              <Section title="Conferences" count={totals.leagues} icon={Trophy}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {results.leagues.map(l => (
                    <ResultCard
                      key={l.id}
                      testId={`search-result-league-${l.id}`}
                      title={l.name}
                      subtitle={l.region ? `${l.region.toUpperCase()} region` : ''}
                      image={l.logo}
                      onClick={() => navigate(`/league/${l.slug}`)}
                    />
                  ))}
                </div>
              </Section>
            )}

            {results.games.length > 0 && (
              <Section title="Games" count={totals.games} icon={Calendar}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {results.games.map(g => (
                    <ResultCard
                      key={g.id}
                      testId={`search-result-game-${g.id}`}
                      title={`${g.home_team_name} vs ${g.away_team_name}`}
                      subtitle={[
                        g.league_name,
                        g.scheduled_time && new Date(g.scheduled_time).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }),
                        g.venue,
                      ].filter(Boolean).join(' • ')}
                      image={g.thumbnail_url}
                      onClick={() => navigate(`/watch/${g.id}`)}
                    />
                  ))}
                </div>
              </Section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const Section = ({ title, count, icon: Icon, children }) => (
  <section className="mb-8">
    <h2 className="flex items-center gap-2 text-lg font-semibold text-white mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
      <Icon className="w-4 h-4 text-sky-400" />
      {title}
      <span className="text-sm text-gray-500 font-normal">({count})</span>
    </h2>
    {children}
  </section>
);

const ResultCard = ({ title, subtitle, image, onClick, testId }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-3 p-3 bg-gray-800/60 hover:bg-gray-800 border border-gray-700/50 hover:border-sky-500/50 rounded-lg text-left transition-colors"
    data-testid={testId}
  >
    <div className="w-12 h-12 bg-gray-900 rounded-md overflow-hidden flex items-center justify-center shrink-0">
      {image ? (
        <img src={image} alt="" className="w-full h-full object-cover" />
      ) : (
        <Search className="w-5 h-5 text-gray-600" />
      )}
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-white text-sm font-medium truncate">{title}</div>
      {subtitle && <div className="text-xs text-gray-400 truncate">{subtitle}</div>}
    </div>
  </button>
);

export default SearchPage;
